import { BASE } from './base';
import { deviceId } from './storage';

export class ApiError extends Error {
  constructor(
    public readonly status: number,
    public readonly code: string,
    message: string,
    public readonly body: Record<string, unknown> = {},
  ) {
    super(message);
    this.name = 'ApiError';
  }
}

export class NetworkError extends Error {
  constructor() {
    super('network');
    this.name = 'NetworkError';
  }
}

type Listener = () => void;
const unauthorizedListeners = new Set<Listener>();
const accessEndedListeners = new Set<Listener>();
/** Fired when the server says the plant's access (trial) has ended. */
export const onAccessEnded = (fn: Listener) => {
  accessEndedListeners.add(fn);
  return () => {
    accessEndedListeners.delete(fn);
  };
};
/** Fired when any API call finds the session gone (expired, revoked, idle timeout). */
export const onUnauthorized = (fn: Listener) => {
  unauthorizedListeners.add(fn);
  return () => {
    unauthorizedListeners.delete(fn);
  };
};

export const API = `${BASE}/api/v1`;

export async function api<T>(
  path: string,
  opts: { method?: string; body?: unknown; form?: FormData; signal?: AbortSignal } = {},
): Promise<T> {
  const method = opts.method ?? (opts.body !== undefined || opts.form ? 'POST' : 'GET');
  const headers: Record<string, string> = {
    'x-clear-client': '1',
    'x-device-id': deviceId(),
    accept: 'application/json',
  };
  if (opts.body !== undefined) headers['content-type'] = 'application/json';
  let res: Response;
  try {
    res = await fetch(`${API}${path}`, {
      method,
      headers,
      credentials: 'same-origin',
      body: opts.form ?? (opts.body !== undefined ? JSON.stringify(opts.body) : undefined),
      signal: opts.signal,
    });
  } catch (e) {
    if ((e as Error).name === 'AbortError') throw e;
    throw new NetworkError();
  }
  if (res.ok) {
    const type = res.headers.get('content-type') ?? '';
    return (type.includes('json') ? await res.json() : undefined) as T;
  }
  let body: Record<string, unknown> = {};
  try {
    body = (await res.json()) as Record<string, unknown>;
  } catch {
    /* not json (e.g. a proxy error page) */
  }
  if (res.status === 401 && !path.startsWith('/auth/login') && !path.startsWith('/auth/pin')) {
    unauthorizedListeners.forEach((fn) => fn());
  }
  if (res.status === 403 && body.code === 'access_ended') accessEndedListeners.forEach((fn) => fn());
  throw new ApiError(res.status, String(body.code ?? 'error'), String(body.title ?? res.statusText), body);
}

export const isNetworkError = (e: unknown): e is NetworkError => e instanceof NetworkError;
